/**
 * API service for marketplace inquiries
 * Handles all inquiry-related API calls including fetching and status updates
 */

import { microservicesClient } from '@/services/microservices/index';
import {
  Inquiry,
  InquiryFilters,
  PaginatedInquiriesResponse,
  UpdateInquiryStatusRequest,
  InquiriesApiError,
  InquiryStatus,
} from '@/modules/marketplace/types/inquiries.types';

const API_BASE = '/api/v1/marketplace/inquiries';

/**
 * Build query string from filters
 */
function buildQueryString(filters: InquiryFilters): string {
  const params = new URLSearchParams();

  // Status filter
  if (filters.status !== 'ALL') {
    params.append('status', filters.status);
  }

  // Product filter
  if (filters.productId) {
    params.append('productId', filters.productId);
  }

  // Pagination
  params.append('page', filters.page.toString());
  params.append('size', filters.pageSize.toString());

  // Sorting
  const sortField = filters.sortBy || 'createdAt';
  const sortOrder = filters.sortOrder?.toUpperCase() || 'DESC';
  params.append('sort', `${sortField},${sortOrder}`);

  return params.toString();
}

/**
 * Fetch inquiries with filters and pagination
 * GET /api/v1/marketplace/inquiries
 */
export async function fetchInquiries(
  filters: InquiryFilters
): Promise<PaginatedInquiriesResponse> {
  try {
    const params: Record<string, any> = {
      page: filters.page.toString(),
      size: filters.pageSize.toString(),
      sort: `${filters.sortBy || 'createdAt'},${(filters.sortOrder || 'DESC').toUpperCase()}`,
    };

    if (filters.status !== 'ALL') {
      params.status = filters.status;
    }
    if (filters.productId) {
      params.productId = filters.productId;
    }
    if (filters.search) {
      params.search = filters.search;
    }

    const response = await microservicesClient.get<{
      content: Inquiry[];
      pageable: any;
      totalElements: number;
      totalPages: number;
      last: boolean;
      size: number;
      number: number;
      sort: any;
      numberOfElements: number;
      first: boolean;
      empty: boolean;
    }>('commerce-service', API_BASE, { params });

    if (!response.success) {
      throw new InquiriesApiError(
        response.error || 'Failed to fetch inquiries',
        response.status
      );
    }

    const data = response.data;

    if (!data || !data.content) {
      throw new InquiriesApiError('Invalid response format', response.status);
    }

    // Ensure response is in expected format
    return {
      content: data.content,
      pageable: data.pageable,
      totalElements: data.totalElements,
      totalPages: data.totalPages,
      last: data.last,
      size: data.size,
      number: data.number,
      sort: data.sort,
      numberOfElements: data.numberOfElements,
      first: data.first,
      empty: data.empty,
    } as PaginatedInquiriesResponse;
  } catch (error) {
    // Error handling below

    if (error instanceof InquiriesApiError) {
      throw error;
    }

    if (error instanceof Error) {
      throw new InquiriesApiError(
        error.message || 'Failed to fetch inquiries',
        undefined,
        'FETCH_INQUIRIES_ERROR'
      );
    }

    throw new InquiriesApiError('Failed to fetch inquiries', undefined, 'FETCH_INQUIRIES_ERROR');
  }
}

/**
 * Get inquiry detail by ID
 * GET /api/v1/marketplace/inquiries/{id}
 */
export async function getInquiryDetail(id: string): Promise<Inquiry> {
  try {
    const response = await microservicesClient.get<Inquiry>(
      'commerce-service',
      `${API_BASE}/${id}`
    );

    if (!response.success) {
      throw new InquiriesApiError(
        response.error || 'Invalid inquiry detail response',
        response.status
      );
    }

    const data = response.data;

    if (!data || !data.id) {
      throw new InquiriesApiError('Invalid inquiry detail response', response.status);
    }

    return data as Inquiry;
  } catch (error) {
    // Error handling below

    if (error instanceof InquiriesApiError) {
      throw error;
    }

    if (error instanceof Error) {
      throw new InquiriesApiError(
        error.message || 'Failed to fetch inquiry detail',
        undefined,
        'FETCH_INQUIRY_DETAIL_ERROR'
      );
    }

    throw new InquiriesApiError(
      'Failed to fetch inquiry detail',
      undefined,
      'FETCH_INQUIRY_DETAIL_ERROR'
    );
  }
}

/**
 * Update inquiry status
 * PUT /api/v1/marketplace/inquiries/{id}/status
 */
export async function updateInquiryStatus(
  id: string,
  status: InquiryStatus,
  notes?: string,
  estimatedQuote?: number
): Promise<Inquiry> {
  try {
    const payload: UpdateInquiryStatusRequest = {
      status,
      ...(notes && { notes }),
      ...(estimatedQuote !== undefined && { estimatedQuote }),
    };

    const response = await microservicesClient.put<Inquiry>(
      'commerce-service',
      `${API_BASE}/${id}/status`,
      payload
    );

    if (!response.success) {
      throw new InquiriesApiError(
        response.error || 'Invalid status update response',
        response.status
      );
    }

    const data = response.data;

    if (!data || !data.id) {
      throw new InquiriesApiError('Invalid status update response', response.status);
    }

    return data as Inquiry;
  } catch (error) {
    // Error handling below

    if (error instanceof InquiriesApiError) {
      throw error;
    }

    if (error instanceof Error) {
      throw new InquiriesApiError(
        error.message || 'Failed to update inquiry status',
        undefined,
        'UPDATE_STATUS_ERROR'
      );
    }

    throw new InquiriesApiError(
      'Failed to update inquiry status',
      undefined,
      'UPDATE_STATUS_ERROR'
    );
  }
}
